import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import FundingMatchFitTierBadge from './FundingMatchFitTierBadge';
import FundingMatchReasons from './FundingMatchReasons';
import { useFundingRoutes } from '../../hooks/funding/useFundingRoutes';

const formatCurrency = (value) => {
  if (value == null || value === '') return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
};

const formatFundingSpeed = (min, max) => {
  if (min == null && max == null) return '—';
  if (min != null && max != null) return `${min}-${max} days`;
  return `${min ?? max} days`;
};

const FundingMatchCard = ({
  match,
  compact = false,
  agencyId,
  onAddToSequence,
  onCreateSubmission,
  adding = false,
  creatingSubmission = false,
  addSuccess = null,
}) => {
  const { sequence: sequencePath, submissions: submissionsPath, lenders: lendersPath } = useFundingRoutes();
  const [localSequenceSuccess, setLocalSequenceSuccess] = useState(null);
  const [localSubmissionSuccess, setLocalSubmissionSuccess] = useState(null);

  if (!match) return null;

  const rangeLabel = match.estimatedAmountMin != null || match.estimatedAmountMax != null
    ? `${formatCurrency(match.estimatedAmountMin)} – ${formatCurrency(match.estimatedAmountMax)}`
    : '—';

  const sequenceSuccessMessage = addSuccess || localSequenceSuccess;

  const handleAddToSequence = async () => {
    if (!onAddToSequence || !agencyId) return;
    try {
      const result = await onAddToSequence(match);
      setLocalSequenceSuccess(result?.sequence?.id ? 'Added to application sequence.' : 'Already in sequence.');
    } catch {
      setLocalSequenceSuccess(null);
    }
  };

  const handleCreateSubmission = async () => {
    if (!onCreateSubmission || !agencyId) return;
    try {
      await onCreateSubmission(match);
      setLocalSubmissionSuccess('Submission created.');
    } catch {
      setLocalSubmissionSuccess(null);
    }
  };

  return (
    <div className="rounded-2xl border border-[var(--fg-line)] bg-[var(--fg-panel-soft)] p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-base font-semibold text-[var(--fg-text)]">{match.lenderName}</div>
          <div className="mt-1 text-sm text-[var(--fg-muted)]">
            {match.lenderType} · {match.productType}
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-[var(--fg-accent)]">{match.matchScore}%</div>
          <div className="text-xs text-[var(--fg-muted)]">match score</div>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <FundingMatchFitTierBadge tier={match.fitTier} />
        <span className="rounded-full bg-white px-2.5 py-1 text-xs font-medium text-[var(--fg-muted)] dark:bg-slate-900">
          Est. {rangeLabel}
        </span>
        <span className="rounded-full bg-white px-2.5 py-1 text-xs font-medium text-[var(--fg-muted)] dark:bg-slate-900">
          Speed {formatFundingSpeed(match.fundingSpeedDaysMin, match.fundingSpeedDaysMax)}
        </span>
      </div>

      {!compact ? (
        <div className="mt-4">
          <FundingMatchReasons
            reasons={match.reasons}
            risks={match.risks}
            missingRequirements={match.missingRequirements}
          />
        </div>
      ) : null}

      {match.recommendedNextAction ? (
        <div className="mt-4 rounded-xl border border-[var(--fg-line)] bg-white px-3 py-2 text-sm text-[var(--fg-text)] dark:bg-slate-950/40">
          <span className="font-semibold text-[var(--fg-accent)]">Recommended: </span>
          {match.recommendedNextAction}
        </div>
      ) : null}

      {sequenceSuccessMessage ? (
        <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-200">
          {sequenceSuccessMessage}{' '}
          <Link to={sequencePath} className="font-semibold underline">Open Sequence</Link>
        </div>
      ) : null}

      {localSubmissionSuccess ? (
        <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-200">
          {localSubmissionSuccess}{' '}
          <Link to={submissionsPath} className="font-semibold underline">Open Submissions</Link>
        </div>
      ) : null}

      {!compact ? (
        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            className="rounded-xl"
            onClick={handleAddToSequence}
            disabled={!agencyId || adding}
          >
            {adding ? 'Adding...' : 'Add to Sequence'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="rounded-xl"
            onClick={handleCreateSubmission}
            disabled={!agencyId || creatingSubmission}
          >
            {creatingSubmission ? 'Creating...' : 'Create Submission'}
          </Button>
          {match.lenderId ? (
            <Link to={`${lendersPath}/${match.lenderId}`}>
              <Button size="sm" variant="ghost" className="rounded-xl">View Lender</Button>
            </Link>
          ) : (
            <Button size="sm" variant="ghost" className="rounded-xl" disabled>View Lender</Button>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default FundingMatchCard;
